import React, { useEffect, useRef, useState } from "react";
import { Camera, Circle, Square, Loader2, Upload, X, CheckCircle2, Repeat, Timer, Mic, MicOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

// -----------------------------------------------------------------------------
// VideoRecorder — records a short intro video in-browser using MediaRecorder,
// then uploads to /api/files/upload and creates a public share URL that the
// caller can plug into any profile.
//
// Props:
//   maxSeconds : integer (default 60)
//   onDone     : (publicUrl:string, meta:{duration_sec, size, mime}) => void
//   audioOnly? : boolean — audio-only mode for accessibility (default false)
// -----------------------------------------------------------------------------
const MIME_CANDIDATES = [
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
  "video/mp4",
];

function pickMime() {
  if (typeof MediaRecorder === "undefined") return "";
  return MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m)) || "";
}

export default function VideoRecorder({ maxSeconds = 60, onDone, audioOnly = false }) {
  const previewRef  = useRef(null);
  const playbackRef = useRef(null);
  const streamRef   = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef   = useRef([]);
  const timerRef    = useRef(null);

  const [status, setStatus] = useState("idle");     // idle|preview|recording|preview_recorded|uploading|done|error
  const [seconds, setSeconds] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const [blob,    setBlob]    = useState(null);
  const [micOn, setMicOn]     = useState(true);
  const [errMsg, setErrMsg]   = useState("");
  const mime = pickMime();

  // ---- start camera preview ------------------------------------------------
  const openCamera = async () => {
    setErrMsg("");
    try {
      const constraints = audioOnly
        ? { audio: true, video: false }
        : { audio: micOn, video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" } };
      const s = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = s;
      if (previewRef.current) {
        previewRef.current.srcObject = s;
        previewRef.current.muted = true;
        await previewRef.current.play().catch(() => {});
      }
      setStatus("preview");
    } catch (e) {
      const msg = String(e?.name || e || "camera_error");
      setErrMsg(
        msg === "NotAllowedError" ? "Camera permission blocked — allow it in your browser settings." :
        msg === "NotFoundError"   ? "No camera detected." :
        `Could not open camera: ${msg}`
      );
      setStatus("error");
    }
  };

  // ---- start recording -----------------------------------------------------
  const startRec = () => {
    if (!streamRef.current) return;
    try {
      chunksRef.current = [];
      const rec = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined);
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const finalBlob = new Blob(chunksRef.current, { type: mime || "video/webm" });
        setBlob(finalBlob);
        const url = URL.createObjectURL(finalBlob);
        setBlobUrl(url);
        setStatus("preview_recorded");
        clearInterval(timerRef.current);
      };
      recorderRef.current = rec;
      rec.start(500);        // small timeslice so ondataavailable fires periodically
      setStatus("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          const next = s + 1;
          if (next >= maxSeconds) stopRec();
          return next;
        });
      }, 1000);
    } catch (e) {
      setErrMsg(String(e?.message || e));
      setStatus("error");
    }
  };

  const stopRec = () => {
    try { recorderRef.current?.stop(); } catch { /* ignore */ }
    clearInterval(timerRef.current);
  };

  // ---- retake / cleanup ----------------------------------------------------
  const retake = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(null); setBlobUrl(null); setSeconds(0); setStatus("preview");
  };

  const closeCamera = () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    streamRef.current = null;
  };

  useEffect(() => () => { closeCamera(); if (blobUrl) URL.revokeObjectURL(blobUrl); }, []);  // eslint-disable-line

  // ---- upload & share ------------------------------------------------------
  const uploadAndShare = async () => {
    if (!blob) return;
    setStatus("uploading");
    try {
      const ext = (mime && mime.includes("mp4")) ? "mp4" : "webm";
      const form = new FormData();
      form.append("file", new File([blob], `intro.${ext}`, { type: blob.type }));
      form.append("label", "video_intro");
      const up = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      const fileId = up.data.file_id;
      const share = await api.post(`/files/${fileId}/share`, { expires_hours: null });
      // Build absolute URL to the public share endpoint (no auth needed).
      const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const publicUrl = `${base}/api/files/share/${share.data.share_id}`;
      setStatus("done");
      closeCamera();
      onDone?.(publicUrl, { duration_sec: seconds, size: blob.size, mime: blob.type });
      toast.success("Video intro uploaded");
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Upload failed";
      setErrMsg(msg);
      setStatus("preview_recorded");
      toast.error(msg);
    }
  };

  const supported = typeof MediaRecorder !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
  if (!supported) {
    return (
      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="video-recorder-unsupported">
        In-browser recording isn't supported on this device. Please paste a public https:// URL instead.
      </div>
    );
  }

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
  const overLimit = seconds >= maxSeconds;

  return (
    <div className="rounded-2xl bg-stone-950 text-white p-3 space-y-2" data-testid="video-recorder">
      {/* Preview stage — LIVE camera */}
      {(status === "idle" || status === "preview" || status === "recording") && (
        <div className="relative rounded-xl overflow-hidden bg-black aspect-video border border-white/10">
          <video ref={previewRef} playsInline muted autoPlay className="w-full h-full object-cover"/>
          {status === "recording" && (
            <div className="absolute top-2 left-2 flex items-center gap-2 rounded-full bg-red-600 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest">
              <Circle size={8} className="fill-white text-white animate-pulse"/> REC · {mmss}
            </div>
          )}
          {status === "recording" && (
            <div className="absolute bottom-2 left-2 right-2 h-1 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-red-500 transition-[width]" style={{ width: `${Math.min(100, (seconds / maxSeconds) * 100)}%` }}/>
            </div>
          )}
        </div>
      )}

      {/* Playback stage — recorded blob */}
      {(status === "preview_recorded" || status === "uploading" || status === "done") && blobUrl && (
        <div className="relative rounded-xl overflow-hidden bg-black aspect-video border border-white/10">
          <video ref={playbackRef} src={blobUrl} controls className="w-full h-full object-contain"/>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {/* Idle → open camera */}
        {status === "idle" && (
          <Button onClick={openCamera} className="rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 font-black h-9" data-testid="video-recorder-open">
            <Camera size={13} className="mr-1"/> Open camera
          </Button>
        )}

        {/* Preview → start recording */}
        {status === "preview" && (
          <>
            <Button onClick={startRec} className="rounded-full bg-red-600 hover:bg-red-700 font-black h-9" data-testid="video-recorder-start">
              <Circle size={12} className="mr-1 fill-white"/> Record ({maxSeconds}s max)
            </Button>
            <Button
              variant="ghost" onClick={() => setMicOn((m) => !m)}
              className="rounded-full h-9 border border-white/10 text-white/70 hover:text-white"
              title={micOn ? "Mute microphone" : "Unmute microphone"}
              data-testid="video-recorder-mic"
            >
              {micOn ? <Mic size={13}/> : <MicOff size={13} className="text-red-400"/>}
            </Button>
            <Button variant="ghost" onClick={() => { closeCamera(); setStatus("idle"); }} className="rounded-full h-9 text-white/70 hover:text-white">
              <X size={13} className="mr-1"/> Cancel
            </Button>
          </>
        )}

        {/* Recording → stop */}
        {status === "recording" && (
          <Button onClick={stopRec} className={`rounded-full ${overLimit ? "bg-amber-600" : "bg-white/10"} hover:bg-white/20 font-black h-9`} data-testid="video-recorder-stop">
            <Square size={12} className="mr-1"/> Stop{overLimit ? " (limit)" : ""}
          </Button>
        )}

        {/* Preview recorded → keep / retake / upload */}
        {status === "preview_recorded" && (
          <>
            <Button onClick={uploadAndShare} className="rounded-full bg-emerald-600 hover:bg-emerald-700 font-black h-9" data-testid="video-recorder-upload">
              <Upload size={13} className="mr-1"/> Use this take
            </Button>
            <Button variant="ghost" onClick={retake} className="rounded-full h-9 text-white/80 border border-white/10 hover:text-white" data-testid="video-recorder-retake">
              <Repeat size={13} className="mr-1"/> Retake
            </Button>
            <span className="text-[11px] text-white/50 flex items-center gap-1"><Timer size={11}/> {mmss}</span>
          </>
        )}

        {status === "uploading" && (
          <div className="text-xs text-white/70 flex items-center gap-2"><Loader2 size={13} className="animate-spin"/> Uploading & generating share link…</div>
        )}

        {status === "done" && (
          <div className="text-xs text-emerald-300 flex items-center gap-1" data-testid="video-recorder-done"><CheckCircle2 size={13}/> Attached to profile</div>
        )}
      </div>

      {errMsg && (
        <div className="text-[11px] text-red-300 bg-red-900/40 border border-red-500/40 rounded-lg px-2 py-1" data-testid="video-recorder-error">{errMsg}</div>
      )}

      <div className="text-[10px] text-white/40">
        Recorded in-browser · nothing is uploaded until you press "Use this take". Camera and mic can be revoked anytime from your browser's site settings.
      </div>
    </div>
  );
}
